// The device side of pairing (docs/protocol.md, "Pairing", steps 3 and 5 to 7).
// Every network dependency is injected so the whole flow runs against a mocked relay in tests.
import {
  decodePairLink,
  fingerprint,
  idFor,
  pairMac,
  sasCode,
  ProtocolError,
  type DaemonMessage,
  type PairInfoResponse,
  type PairJoinRequest,
  type PairLink,
  type RelayFrame,
} from "@whistle/protocol";
import type { DeviceIdentity } from "./identity";
import type { PairingRecord } from "./model";
import { RelayClient } from "./relay";
import { displayLine } from "./sanitize";
import { openFromMachine, sealToMachine } from "./transport";

export type PairErrorCode = "bad-link" | "relay" | "network" | "expired" | "taken" | "device-limit" | "mismatch" | "bad-response" | "rejected" | "timeout" | "cancelled";

export class PairError extends Error {
  readonly code: PairErrorCode;
  constructor(code: PairErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "PairError";
  }
}

export function parsePairLink(hash: string): PairLink {
  try {
    return decodePairLink(hash);
  } catch (e) {
    throw new PairError("bad-link", e instanceof ProtocolError ? "This isn't a complete pairing link. Scan the code again." : "Unreadable pairing link.");
  }
}

/** What a pairing link points at, checked against the relay's answer: shown before anything is replaced. */
export type PairTarget = { machineName: string; machineId: string; accountId: string; relayOrigin: string; fingerprint: string };

export function pairTarget(link: PairLink, info: PairInfoResponse): PairTarget {
  return { machineName: displayLine(info.machine.name, 80), machineId: link.m, accountId: link.a, relayOrigin: link.r, fingerprint: link.f };
}

const originKey = (origin: string) => origin.replace(/\/+$/, "").toLowerCase();

/**
 * The same machine key, account and relay (security review 2, S1). A name proves nothing: anyone can build a link that
 * names "Desk PC", and anyone can run a relay.
 */
export function samePairing(a: Pick<PairingRecord, "machineId" | "accountId" | "relayOrigin">, b: Pick<PairTarget, "machineId" | "accountId" | "relayOrigin">): boolean {
  return a.machineId === b.machineId && a.accountId === b.accountId && originKey(a.relayOrigin) === originKey(b.relayOrigin);
}

export const sameRelay = (a: string, b: string) => originKey(a) === originKey(b);

declare const __WHISTLE_RELAYS__: string[] | null | undefined;

/** The relays this build talks to, the same list as its CSP connect-src (vite.config.ts). null: any (dev and tests). */
export const BUILD_RELAYS: readonly string[] | null = typeof __WHISTLE_RELAYS__ === "undefined" ? null : __WHISTLE_RELAYS__;

/** Security review 2, S1: a pairing link for a relay the build doesn't name is refused before anything is fetched. */
export function checkRelayAllowed(origin: string, allowed: readonly string[] | null = BUILD_RELAYS): void {
  if (!allowed || allowed.some((o) => sameRelay(o, origin))) return;
  throw new PairError("relay", `This link sends the phone through ${relayHost(origin)}. This app connects only through ${allowed.map(relayHost).join(" or ")}.`);
}

/** The relay's host name for the screen: "whistle-relay.example.workers.dev". */
export function relayHost(origin: string): string {
  try {
    return new URL(origin).host || origin;
  } catch {
    return displayLine(origin, 80);
  }
}

/** A machine fingerprint in groups of four, so two can be compared by eye. */
export const groupFingerprint = (f: string) => (f.match(/.{1,4}/g) ?? []).join(" ");

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

/** Step 3a: fetch the machine's public identity and check it against the fingerprint in the link. */
export async function fetchPairInfo(link: PairLink, fetchImpl: FetchLike = fetch): Promise<PairInfoResponse> {
  let res: Response;
  try {
    res = await fetchImpl(`${link.r.replace(/\/+$/, "")}/v1/pair/${encodeURIComponent(link.p)}?a=${encodeURIComponent(link.a)}`, { method: "GET", credentials: "omit", cache: "no-store" });
  } catch {
    throw new PairError("network", "Couldn't reach the relay. Check the phone's connection and try again.");
  }
  if (res.status === 404 || res.status === 410) throw new PairError("expired", "This code has expired. Press Enter at your desk for a new one.");
  if (res.status === 409) throw new PairError("taken", "This code was already used. Press Enter at your desk for a new one.");
  if (!res.ok) throw new PairError("bad-response", `The relay answered ${res.status}. Try again in a moment.`);
  let info: PairInfoResponse;
  try {
    info = (await res.json()) as PairInfoResponse;
  } catch {
    throw new PairError("bad-response", "The relay sent something unreadable.");
  }
  if (!info?.machine?.sigPub || !info.machine.encPub || typeof info.machine.name !== "string") throw new PairError("bad-response", "The relay sent an incomplete answer.");
  if (info.accountId && info.accountId !== link.a) throw new PairError("mismatch", "This code doesn't match your computer.");
  if ((await fingerprint(info.machine)) !== link.f) throw new PairError("mismatch", "This code doesn't match your computer.");
  if ((await idFor(info.machine.sigPub)) !== link.m) throw new PairError("mismatch", "This code doesn't match your computer.");
  return info;
}

/** Step 3b and 3c: prove we saw the secret and join. Returns the SAS to show. */
export async function joinPairing(link: PairLink, info: PairInfoResponse, identity: DeviceIdentity, deviceName: string, fetchImpl: FetchLike = fetch): Promise<{ sas: string }> {
  const mac = await pairMac(link.s, link.p, identity.pub.sigPub, identity.pub.encPub, deviceName);
  const body: PairJoinRequest = { device: { sigPub: identity.pub.sigPub, encPub: identity.pub.encPub, name: deviceName }, mac };
  let res: Response;
  try {
    res = await fetchImpl(`${link.r.replace(/\/+$/, "")}/v1/pair/${encodeURIComponent(link.p)}/join?a=${encodeURIComponent(link.a)}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
    });
  } catch {
    throw new PairError("network", "Couldn't reach the relay. Check the phone's connection and try again.");
  }
  if (res.status === 404 || res.status === 410) throw new PairError("expired", "This code has expired. Press Enter at your desk for a new one.");
  if (!res.ok) {
    // The relay names its refusals. A used code is a 404 above, so its 409 today is the one-phone limit.
    const code = await relayErrorCode(res);
    // The relay supplied the name; our own text marks commands with backticks, so it may not carry any.
    const machine = displayLine(info.machine.name, 80).replace(/`/g, "'");
    if (code === "device_limit") throw new PairError("device-limit", `${machine} already has a phone paired. Remove it at your desk with \`whistle revoke\`, then pair again.`);
    if (res.status === 409) throw new PairError("taken", "Another phone already joined with this code. Press Enter at your desk for a new one.");
    if (code === "machine_offline") throw new PairError("network", `${machine} isn't connected to the relay right now. Check \`whistle status\` at your desk, then press Enter there for a new code.`);
    throw new PairError("bad-response", `The relay answered ${res.status}. Try again in a moment.`);
  }
  const sas = await sasCode(link.s, info.machine, identity.pub);
  return { sas };
}

export type WaitOptions = {
  WebSocketImpl?: typeof WebSocket;
  timeoutMs?: number;
  signal?: AbortSignal;
  now?: () => number;
};

/**
 * Steps 6 and 7: connect to the relay and wait for the machine's encrypted `paired` message.
 * The relay rejects our key (4001) until the daemon has added us, so we keep retrying quietly.
 * A `state` message from the machine counts too: it proves the same thing, and covers a `paired`
 * that was sent before this socket was up.
 */
export function waitForPaired(link: PairLink, info: PairInfoResponse, identity: DeviceIdentity, deviceName: string, opts: WaitOptions = {}): Promise<PairingRecord> {
  const provisional = { machineId: link.m, machine: info.machine, deviceId: identity.deviceId };
  return new Promise<PairingRecord>((resolve, reject) => {
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      opts.signal?.removeEventListener("abort", onAbort);
      client.stop();
      fn();
    };
    const onAbort = () => finish(() => reject(new PairError("cancelled", "Pairing cancelled.")));
    const timer = setTimeout(() => finish(() => reject(new PairError("timeout", "Your computer didn't confirm in time. Start pairing again at your desk."))), opts.timeoutMs ?? 10 * 60_000);
    opts.signal?.addEventListener("abort", onAbort);

    const client = new RelayClient({
      relayOrigin: link.r,
      accountId: link.a,
      deviceId: identity.deviceId,
      signKey: identity.identity.sig.privateKey,
      WebSocketImpl: opts.WebSocketImpl,
      backoffBaseMs: 1500,
      backoffMaxMs: 6000,
      onAuthRejected: () => "retry",
      onStatus: () => {},
      onFrame: (frame: RelayFrame) => {
        if (frame.t === "ready") {
          // Ask for state in case `paired` went out before we were connected. Sealing needs the counter store,
          // which is fine: the pairing record below uses the same machineId.
          void sealToMachine({ type: "hello" }, provisional, identity, opts.now?.())
            .then((env) => client.send({ t: "msg", to: link.m, env }))
            .catch(() => {});
          return;
        }
        if (frame.t !== "msg" || frame.from !== link.m) return;
        void openFromMachine<DaemonMessage>(frame.env, provisional, identity, opts.now?.())
          .then((msg) => {
            if (msg.type !== "paired" && msg.type !== "state") return;
            const name = displayLine(msg.machine?.name, 80) || displayLine(info.machine.name, 80);
            finish(() =>
              resolve({
                accountId: link.a,
                machineId: link.m,
                machine: { ...info.machine, name },
                relayOrigin: link.r,
                deviceId: identity.deviceId,
                deviceName,
                pairedAt: opts.now?.() ?? Date.now(),
              }),
            );
          })
          .catch(() => {
            /* a bad envelope is dropped; keep waiting */
          });
      },
    });
    client.start();
  });
}

async function relayErrorCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { error?: unknown } | null;
    return typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

/**
 * True when a code that can no longer be used came from the machine this phone is already paired with: the page was
 * reloaded after pairing (a server swap, Chrome restoring the tab) or the old link was opened again. The pairing
 * worked, so the screen offers the inbox rather than "expired".
 */
export function isOwnPairing(hash: string, pairing: PairingRecord | null, err: unknown): boolean {
  if (!pairing || !(err instanceof PairError) || (err.code !== "expired" && err.code !== "taken")) return false;
  try {
    const link = parsePairLink(hash);
    return link.m === pairing.machineId && link.a === pairing.accountId;
  } catch {
    return false;
  }
}

/** A model name worth showing, or null for a model number or the reduced User-Agent's placeholder "K". */
function nameFromModel(raw: string): string | null {
  const model = raw.trim();
  if (/^SM-/.test(model)) return "Samsung phone";
  if (model.length < 2 || model.length > 24 || /^[A-Z]{1,3}\d{3,}/.test(model)) return null;
  return model;
}

/** A readable default device name from the user agent: "Pixel 8", "Samsung phone", "Android phone". */
export function defaultDeviceName(ua: string = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  const m = /Android [\d.]+; ([^;)]+?)(?: Build|\))/.exec(ua);
  const named = m?.[1] ? nameFromModel(m[1]) : null;
  if (named) return named;
  if (/Android/.test(ua)) return "Android phone";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/Windows/.test(ua)) return "Windows browser";
  return "Phone";
}

type UaHints = { getHighEntropyValues(hints: string[]): Promise<{ model?: string }> };
type NavigatorLike = { userAgent: string; userAgentData?: UaHints };

/**
 * The default name, asking Client Hints for the model first: Chrome on Android puts "K" where the model was in its
 * User-Agent string, and only getHighEntropyValues still has it.
 */
export async function guessDeviceName(nav: NavigatorLike | undefined = typeof navigator !== "undefined" ? (navigator as NavigatorLike) : undefined): Promise<string> {
  try {
    const hints = await nav?.userAgentData?.getHighEntropyValues(["model"]);
    const named = hints?.model ? nameFromModel(hints.model) : null;
    if (named) return named;
  } catch {
    /* hints refused: fall back to the User-Agent guess */
  }
  return defaultDeviceName(nav?.userAgent ?? "");
}
